import React, { useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'sonner';
import { Heart, CreditCard, Building2, Check, Shield, ArrowRight, ArrowLeft, Copy } from 'lucide-react';
import { DONATION_TIERS, BANK_INFO } from '../data/mock';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import PageHero from '../components/PageHero';
import { usePublicBankAccounts } from '../hooks/usePublicSettings';
import EditableList from '../components/EditableList';

export default function Donate() {
  useSEO('donate');
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [info, setInfo] = useState({ name: '', surname: '', email: '', phone: '', city: 'Istanbul' });
  const [payment, setPayment] = useState({ number: '', exp: '', cvc: '', holder: '' });
  const [method, setMethod] = useState('card');
  const [selectedBank, setSelectedBank] = useState(null);

  const banksFromDb = usePublicBankAccounts();
  const banks = banksFromDb.length > 0 ? banksFromDb : BANK_INFO;

  const final = custom ? parseInt(custom) : amount;

  const next = () => {
    if (step === 0 && final < 10) { toast.error('Lütfen bir tutar seçin.'); return; }
    if (step === 1 && (!info.name || !info.email)) { toast.error('Ad ve e-posta zorunludur.'); return; }
    setStep(s => s + 1);
  };

  const payWithCard = async () => {
    if (!info.name || !info.email) { toast.error('Önce kişisel bilgileri doldurun.'); return; }
    try {
      const res = await api.iyzicoInitiate({
        amount: final,
        recurring,
        name: info.name, surname: info.surname || '',
        email: info.email, phone: info.phone || '',
        address: info.city || 'Türkiye', city: info.city || 'Istanbul',
      });
      if (res.paymentPageUrl) {
        // Save id for return page
        localStorage.setItem('yekuv_last_payment', res.paymentId);
        window.location.href = res.paymentPageUrl;
      } else if (res.checkoutFormContent) {
        // Inline mode — embed iyzico form
        const div = document.createElement('div');
        div.id = 'iyzipay-checkout-form';
        div.className = 'responsive';
        document.body.appendChild(div);
        // eslint-disable-next-line no-new-func
        new Function(res.checkoutFormContent)();
      } else {
        toast.error('Ödeme sayfası açılamadı.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Ödeme başlatılamadı.';
      toast.error(msg);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    payWithCard();
  };

  const submitHavale = async () => {
    if (!selectedBank) { toast.error('Lütfen havale yaptığınız bankayı seçin.'); return; }
    try {
      await api.createDonation({
        amount: final,
        recurring,
        method: 'havale',
        bank: { bank: selectedBank.bank, iban: selectedBank.iban, currency: selectedBank.currency || 'TL' },
        note: 'Havale/EFT bildirimi',
        info: { name: info.name, surname: info.surname || '', email: info.email, phone: info.phone || '', city: info.city || 'Istanbul' },
      });
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bildirim gönderilemedi.');
    }
  };

  return (
    <EditRegion label="Online Bağış" to="/admin/settings">
      <>
      <PageHero slug="donate"
        kicker="Bağış Yap"
        title={<>Hayata <span className="italic text-coral-200">dokunan</span> bir adım atın.</>}
        text="Her bağış, bir çocuğun daha eğitimine destek olur. Şeffaf, güvenli ve hızlı bağış."
      />

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            {/* Progress */}
            <div className="flex items-center gap-3 mb-8">
              {['Miktar', 'Bilgiler', 'Ödeme'].map((label, i) => (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold transition-colors ${step >= i ? 'bg-ink-600 text-cream' : 'bg-cream-200 text-ink-400'}`}>
                      {step > i ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-sm ${step >= i ? 'text-ink-600 font-medium' : 'text-ink-400'}`}>{label}</span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-px ${step > i ? 'bg-ink-600' : 'bg-ink-100'}`} />}
                </React.Fragment>
              ))}
            </div>

            {step === 0 && (
              <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                <h2 className="font-display text-3xl text-ink-600">Bağış tutarı seçin</h2>

                {/* Frequency toggle — prominent */}
                <div className="mt-5 grid grid-cols-2 gap-2 p-1.5 bg-cream-200 rounded-2xl border border-ink-100">
                  <button onClick={() => setRecurring(false)} data-testid="freq-once" className={`py-3 rounded-xl text-sm font-semibold transition-all ${!recurring ? 'bg-white text-ink-600 shadow-sm' : 'text-ink-400 hover:text-ink-600'}`}>
                    Tek Seferlik
                  </button>
                  <button onClick={() => setRecurring(true)} data-testid="freq-monthly" className={`py-3 rounded-xl text-sm font-semibold transition-all relative ${recurring ? 'bg-ink-600 text-cream shadow-md' : 'text-ink-400 hover:text-ink-600'}`}>
                    Aylık Düzenli <span className="absolute -top-2 -right-2 bg-coral-300 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider">Etki x12</span>
                  </button>
                </div>
                {recurring && (
                  <div className="mt-3 p-3 rounded-xl bg-gold/10 border border-gold/30 text-xs text-ink-600">
                    <strong>Düzenli destek</strong> ile her ay aynı tutarda bir öğrenciye umut olursunuz. Dilediğiniz zaman iptal edebilirsiniz.
                  </div>
                )}

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {DONATION_TIERS.map(t => (
                    <button key={t} onClick={() => { setAmount(t); setCustom(''); }} className={`h-16 rounded-2xl border-2 font-display text-xl tabular transition-colors ${amount === t && !custom ? 'border-ink-600 bg-ink-600 text-cream' : 'border-ink-100 bg-cream-200 text-ink-600 hover:border-ink-300'}`}>
                      ₺{t.toLocaleString('tr-TR')}{recurring && <span className="block text-[10px] tracking-wider opacity-70 mt-0.5">/AY</span>}
                    </button>
                  ))}
                </div>
                <div className="mt-6">
                  <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">Diğer bir tutar{recurring ? ' (aylık)' : ''}</label>
                  <div className="mt-1.5 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400 font-display text-xl">₺</span>
                    <input type="number" value={custom} onChange={e => setCustom(e.target.value)} placeholder="0" className="w-full h-14 pl-10 pr-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-ink-600 font-display text-xl tabular" />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <button onClick={next} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500 transition-colors">Devam <ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                <h2 className="font-display text-3xl text-ink-600">Kişisel bilgileriniz</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <TField label="Ad *" value={info.name} onChange={v => setInfo({ ...info, name: v })} />
                  <TField label="Soyad" value={info.surname} onChange={v => setInfo({ ...info, surname: v })} />
                  <TField label="E-posta *" type="email" value={info.email} onChange={v => setInfo({ ...info, email: v })} />
                  <TField label="Telefon" value={info.phone} onChange={v => setInfo({ ...info, phone: v })} />
                  <TField label="Şehir" value={info.city} onChange={v => setInfo({ ...info, city: v })} />
                </div>
                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                  <button onClick={next} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500">Devam <ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={complete} className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                <h2 className="font-display text-3xl text-ink-600">Ödeme Yöntemi</h2>
                <p className="mt-3 text-ink-500 text-sm">Bağışınızı nasıl yapmak istediğinizi seçin.</p>

                {/* Payment method selector */}
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setMethod('card')} data-testid="pay-method-card"
                    className={`p-5 rounded-2xl border-2 text-left transition-all ${method === 'card' ? 'border-ink-600 bg-ink-600 text-cream shadow-lg' : 'border-ink-100 bg-cream-200 text-ink-600 hover:border-ink-300'}`}>
                    <CreditCard className={`w-6 h-6 ${method === 'card' ? 'text-gold' : 'text-ink-400'}`} />
                    <div className="mt-3 font-display text-xl">Kredi Kartı</div>
                    <div className={`mt-1 text-xs ${method === 'card' ? 'text-cream/70' : 'text-ink-400'}`}>iyzico güvenli ödeme · 3D Secure</div>
                  </button>
                  <button type="button" onClick={() => setMethod('havale')} data-testid="pay-method-havale"
                    className={`p-5 rounded-2xl border-2 text-left transition-all ${method === 'havale' ? 'border-ink-600 bg-ink-600 text-cream shadow-lg' : 'border-ink-100 bg-cream-200 text-ink-600 hover:border-ink-300'}`}>
                    <Building2 className={`w-6 h-6 ${method === 'havale' ? 'text-gold' : 'text-ink-400'}`} />
                    <div className="mt-3 font-display text-xl">Havale / EFT</div>
                    <div className={`mt-1 text-xs ${method === 'havale' ? 'text-cream/70' : 'text-ink-400'}`}>Banka hesabına transfer</div>
                  </button>
                </div>

                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  <div className="rounded-xl bg-cream-200 p-4 border border-ink-100"><div className="text-[10px] uppercase tracking-widest text-coral-300 font-bold">Tutar</div><div className="font-display text-2xl tabular text-ink-600 mt-1">₺{final.toLocaleString('tr-TR')}</div></div>
                  <div className="rounded-xl bg-cream-200 p-4 border border-ink-100"><div className="text-[10px] uppercase tracking-widest text-coral-300 font-bold">Bağışçı</div><div className="text-sm text-ink-600 mt-1 truncate">{info.name} {info.surname}</div></div>
                  <div className="rounded-xl bg-cream-200 p-4 border border-ink-100"><div className="text-[10px] uppercase tracking-widest text-coral-300 font-bold">E-posta</div><div className="text-sm text-ink-600 mt-1 truncate">{info.email}</div></div>
                </div>

                {method === 'card' && (
                  <>
                    <p className="mt-6 text-ink-500 text-sm">"Bağışı Tamamla" düğmesine tıkladığınızda <span className="font-semibold text-ink-600">iyzico</span> güvenli ödeme sayfasına yönlendirileceksiniz. Tüm büyük banka kartları, taksit seçenekleri ve 3D Secure desteklenir.</p>
                    <div className="mt-6 flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                      <Shield className="w-5 h-5 text-emerald-600 shrink-0" />
                      <div className="text-xs text-emerald-700">256-bit SSL · 3D Secure · iyzico altyapısı · Kart bilgileriniz YEKÜV sunucusunda saklanmaz, doğrudan iyzico tarafından işlenir.</div>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-ink-400">
                      <span className="font-semibold">Kabul edilen kartlar:</span>
                      <span className="px-2 py-1 rounded bg-cream-200 font-mono">Visa</span>
                      <span className="px-2 py-1 rounded bg-cream-200 font-mono">MasterCard</span>
                      <span className="px-2 py-1 rounded bg-cream-200 font-mono">American Express</span>
                      <span className="px-2 py-1 rounded bg-cream-200 font-mono">Troy</span>
                    </div>
                    <div className="mt-8 flex justify-between">
                      <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                      <button type="submit" data-testid="iyzico-pay-btn" className="inline-flex items-center gap-2 bg-coral-300 text-ink-700 rounded-full px-8 h-12 font-semibold hover:bg-coral-400 hover:text-cream transition-colors">
                        <CreditCard className="w-4 h-4" /> ₺{final.toLocaleString('tr-TR')} Bağışı Tamamla
                      </button>
                    </div>
                  </>
                )}

                {method === 'havale' && (
                  <>
                    <p className="mt-6 text-ink-500 text-sm">Aşağıdaki hesaplardan birine <span className="font-semibold text-ink-600">₺{final.toLocaleString('tr-TR')}</span> tutarında havale/EFT yapabilirsiniz. <span className="font-semibold text-ink-600">Havale yaptığınız bankayı seçin</span> ve açıklama kısmına ad-soyadınızı yazmayı unutmayın.</p>
                    <div className="mt-5 space-y-3" data-testid="havale-bank-list">
                      {banks.map((b, i) => {
                        const isSel = selectedBank && selectedBank.iban === b.iban;
                        return (
                        <button type="button" key={b.id || i} onClick={() => setSelectedBank(b)} data-testid={`havale-bank-${i}`}
                          className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${isSel ? 'border-ink-600 bg-ink-600/5 ring-2 ring-ink-600/20' : 'border-ink-100 bg-cream-200 hover:border-ink-300'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full grid place-items-center border-2 shrink-0 ${isSel ? 'border-ink-600 bg-ink-600' : 'border-ink-300 bg-white'}`}>
                                {isSel && <Check className="w-3 h-3 text-cream" />}
                              </span>
                              <div className="font-semibold text-ink-600">{b.bank}</div>
                            </div>
                            <span className="text-[10px] font-semibold uppercase tracking-widest bg-gold/20 text-gold-500 rounded-full px-2 py-1">{b.currency}</span>
                          </div>
                          {b.branch && <div className="text-xs text-ink-400 ml-7">{b.branch}</div>}
                          <div className="mt-2 ml-7 flex items-center justify-between gap-2 flex-wrap">
                            <div className="font-mono text-sm text-ink-600 tabular break-all">{b.iban}</div>
                            <span role="button" tabIndex={0} data-testid={`copy-iban-${i}`}
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText((b.iban || '').replace(/\s/g, '')); toast.success('IBAN kopyalandı'); }}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-ink-600 text-cream rounded-full px-3 h-8 hover:bg-ink-500 shrink-0 cursor-pointer">
                              <Copy className="w-3 h-3" /> Kopyala
                            </span>
                          </div>
                        </button>
                        );
                      })}
                    </div>
                    <div className="mt-5 flex items-center gap-3 p-4 rounded-xl bg-gold/10 border border-gold/30">
                      <Shield className="w-5 h-5 text-gold-500 shrink-0" />
                      <div className="text-xs text-ink-600">Havalenizi yaptıktan sonra "Havale Bildirimi Gönder" butonuna tıklayın; bağışınız kayıtlarımıza işlensin ve makbuzunuz hazırlansın.</div>
                    </div>
                    <div className="mt-8 flex justify-between">
                      <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                      <button type="button" onClick={submitHavale} data-testid="havale-submit-btn" className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500 transition-colors">
                        <Building2 className="w-4 h-4" /> Havale Bildirimi Gönder
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {step === 3 && (
              <div className="bg-white rounded-3xl p-12 border border-ink-100 shadow-xl text-center fade-up">
                <div className="w-20 h-20 rounded-full bg-gold/20 grid place-items-center mx-auto text-gold-500"><Check className="w-10 h-10" /></div>
                <h2 className="font-display text-4xl text-ink-600 mt-6">Teşekkür ederiz!</h2>
                <p className="mt-3 text-ink-500 max-w-md mx-auto">
                  {method === 'havale'
                    ? `₺${final.toLocaleString('tr-TR')} tutarındaki havale bildiriminiz alındı. Ödemeniz hesabımıza ulaştığında makbuzunuz ${info.email} adresine gönderilecek.`
                    : `₺${final.toLocaleString('tr-TR')} bağışınız alındı. Makbuzunuz ${info.email} adresine gönderilecek.`}
                </p>
                <button onClick={() => { setStep(0); setAmount(500); setCustom(''); }} className="mt-8 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-11 font-medium">Yeni Bağış Yap</button>
              </div>
            )}
          </div>

          {/* Summary / Bank Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-ink-600 text-cream rounded-3xl p-8">
              <div className="text-[10px] uppercase tracking-widest text-gold">Bağış Özeti</div>
              <div className="font-display text-5xl tabular mt-3">₺{final.toLocaleString('tr-TR')}</div>
              <div className="mt-2 text-cream/70 text-sm">{recurring ? 'Aylık tekrarlayan' : 'Tek seferlik'}</div>
              <div className="h-px bg-cream/10 my-6" />
              <EditableList
                settingKey="donate_summary_bullets"
                defaultItems={[
                  'Vergi avantajı sağlanır (kamu yararı statüsü)',
                  'Bağış makbuzu e-posta ile gönderilir',
                  'Harcama raporlarına şeffaf erişim',
                ]}
                addLabel="Yeni fayda ekle"
                listAs="ul"
                className="space-y-3 text-sm text-cream/80"
                itemClassName="pr-16"
                renderItem={(text) => (
                  <li className="flex gap-2">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="flex-1 whitespace-pre-wrap">{text}</span>
                  </li>
                )}
              />
            </div>

            <div id="banka" className="bg-white rounded-3xl p-8 border border-ink-100">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-coral-300 font-semibold">
                <Building2 className="w-4 h-4" /> Banka Hesap Bilgileri
              </div>
              <h3 className="font-display text-2xl mt-2 text-ink-600">Havale / EFT ile bağış</h3>
              <div className="mt-5 space-y-4" data-testid="donate-bank-list">
                {banks.map((b, i) => (
                  <EditRegion key={b.id || i} label="Banka Hesabını Düzenle" to="/admin/bank-accounts">
                    <div className="p-4 rounded-2xl bg-cream-200 border border-ink-100">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-ink-600">{b.bank}</div>
                        <span className="text-[10px] font-semibold uppercase tracking-widest bg-gold/20 text-gold-500 rounded-full px-2 py-1">{b.currency}</span>
                      </div>
                      {b.branch && <div className="text-xs text-ink-400">{b.branch}</div>}
                      <div className="mt-2 font-mono text-sm text-ink-600 tabular break-all">{b.iban}</div>
                    </div>
                  </EditRegion>
                ))}
              </div>
              {/* Admin add-bank CTA (visible in edit mode) */}
              <div className="yekuv-admin-only mt-5 pt-5 border-t border-ink-100" style={{ display: 'none' }}>
                <a
                  href="/admin/bank-accounts"
                  data-testid="donate-add-bank"
                  className="inline-flex items-center gap-2 text-xs h-9 px-4 rounded-full bg-ink-600 text-cream font-semibold hover:bg-ink-500"
                >
                  + Banka Ekle / Düzenle
                </a>
                <p className="mt-2 text-[10px] text-ink-400 leading-relaxed">
                  Buradaki banka listesi <strong>Admin Panel → Banka Hesapları</strong> üzerinden yönetilir; eklediğin/sildiğin bankalar anında burada da güncellenir.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function TField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="mt-1.5 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600" />
    </div>
  );
}
